import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { useEffect } from 'react';
import moment from 'moment';
import { t } from 'i18next';
import CustomSelect from '@/app/components/CustomSelect';

export function BulletinBoardsCreateContent({ isEditing, bannerData }) {
  const navigate = useNavigate();
  const client = useClient();
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  useEffect(() => {
    if (isEditing && bannerData) {
      bannerForm.setValues({
        image: bannerData?.image.url || null,
        title: bannerData?.title,
        profiles: bannerData?.profiles || [],
        link: bannerData?.link || '',
        startDate: bannerData['start-date'],
        endDate: bannerData['end-date'],
        publish: bannerData?.['published-at'] === null ? false : true,
        textButtonLink: bannerData?.label || ''
      });
    }
    return () => {
      bannerForm.resetForm();
    };
  }, [isEditing, bannerData]);

  const submitForm = async () => {
    const payload = {
      image: bannerForm.values.image,
      title: bannerForm.values.title,
      'profile-ids': bannerForm.values.profiles.some(i => i.label === 'Todos') ? profiles.map(profile => profile.id) : bannerForm.values.profiles.map(profile => profile.value || profile.id),
      link: bannerForm.values.link,
      'start-date': bannerForm.values.startDate,
      'end-date': bannerForm.values.endDate,
      'published-at': bannerForm.values.publish ? new Date() : null,
      label: bannerForm.values.textButtonLink
    };

    if (isEditing) {
      const { error } = await client.mutate(`bulletin_boards/${bannerData.id}`, payload, {
        method: 'PUT'
      });
      if (error) {
        toast.error(t('bulletinBoards.errorUpdateBanner'));
      } else {
        toast.success(t('bulletinBoards.successUpdateBanner'));
        navigate('/banners');
      }
    } else {
      const { data, error } = await client.mutate('bulletin_boards', payload, {
        method: 'POST'
      });
      if (error) {
        toast.error(t('bulletinBoards.errorCreateBanner'));
      } else {
        toast.success(t('bulletinBoards.successCreateBanner'));
        navigate(`/banners`);
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    bannerForm.setFieldTouched('image', true);
    bannerForm.setFieldTouched('title', true);
    bannerForm.setFieldTouched('profiles', true);
    bannerForm.setFieldTouched('startDate', true);
    bannerForm.setFieldTouched('endDate', true);
    bannerForm.setFieldTouched('link', true);
    bannerForm.setFieldTouched('textButtonLink', true);

    if (bannerForm.values.title && bannerForm.values.startDate && bannerForm.values.endDate) {
      submitForm();
    }
  };

  const bannerForm = useFormik({
    initialValues: {
      image: null,
      title: '',
      profiles: [],
      link: '',
      startDate: '',
      endDate: '',
      publish: false,
      textButtonLink: ''
    },
    onSubmit: handleSubmit,
    validationSchema: yup.object({
      title: yup.string().required(t('warning.requiredField')),
      profiles: yup.array().min(1, t('warning.requiredField')),
      link: yup.string(),
      startDate: yup.string().required(t('warning.requiredField')),
      endDate: yup.string().required(t('warning.requiredField')),
      publish: yup.boolean(),
      textButtonLink: yup.string()
    })
  });

  const handleChangeImage = e => {
    let file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = _handleReaderLoaded.bind(this);
      reader.readAsBinaryString(file);
    }
  };

  const _handleReaderLoaded = readerEvt => {
    let bynaryString = readerEvt.target.result;
    bannerForm.setFieldValue('image', `data:image/jpeg;base64,${btoa(bynaryString)}`);
  };

  const getProfilesList = async () => {
    setLoadingProfiles(true);
    try {
      const { data, error } = await client.fetch('/profiles');
      if (error) {
        toast.error(t('warning.errorFetchingProfiles'));
      } else {
        setProfiles(data);
      }
    } catch (error) {
      toast.error(t('warning.errorFetchingProfiles'));
    }
    setLoadingProfiles(false);
  };

  useEffect(() => {
    getProfilesList();
  }, []);

  return (
    <div
      className="page page--wrap"
      style={{ maxWidth: '700px' }}
    >
      <div className="page__col">
        <form
          className="form form--step-by-step"
          method="post"
          onSubmit={handleSubmit}
        >
          <div className="page__content mb-12 animate-fade-in">
            <div className="new-questions__header">
              <h2 className="new-questions__title">{isEditing ? t('bulletinBoards.editBanner') : t('bulletinBoards.newBanner')}</h2>
            </div>
            <div>
              <fieldset className="form__row">
                <legend className="form__label">{t('textsCommon.image')} (1020x252 pixels)</legend>
                <label className="form__custom-file-input">
                  <input
                    type="file"
                    name="image"
                    id="image"
                    accept=".jpeg, .png, .jpg"
                    onChange={handleChangeImage}
                    // defaultValue={}
                  />
                  <span className="form__control">{t('bulletinBoards.filePlaceholder')}</span>
                </label>
              </fieldset>
              {bannerForm.values.image && (
                <div className="card__header-img-wrapper">
                  <img
                    className="card__header-img"
                    src={bannerForm?.values?.image}
                    alt="Banner"
                    style={{ height: '140px' }}
                  />
                </div>
              )}
              <div className="form__row">
                <label
                  className="form__label"
                  htmlFor="title"
                >
                  *{t('bulletinBoards.bannerTitle')}
                </label>
                <input
                  className="form__control"
                  placeholder={t('bulletinBoards.titlePlaceholder')}
                  id="title"
                  name="title"
                  type="text"
                  value={bannerForm.values.title}
                  onChange={e => bannerForm.setFieldValue('title', e.target.value)}
                />
                {bannerForm.touched.title && bannerForm.errors.title && <span style={{ color: 'red' }}>{bannerForm.errors.title}</span>}
              </div>
              <div className="form__row">
                <label
                  className="form__label"
                  htmlFor="course"
                >
                  {t('bulletinBoards.associateProfiles')}
                </label>
                <CustomSelect
                  className="filter-bar__multi-select u-w-100"
                  isMulti
                  isDisabled={loadingProfiles}
                  value={bannerForm.values.profiles}
                  options={profiles}
                  placeholder={t('bulletinBoards.profilesPlaceholder')}
                  onChange={e => {
                    bannerForm.setFieldValue('profiles', e);
                  }}
                />
              </div>
              <span className="form__label">Link</span>
              <div
                className="form__row form__row--columns"
                style={{ marginTop: '0.5rem' }}
              >
                <div className="form__col">
                  <input
                    className="form__control"
                    placeholder={t('bulletinBoards.linkPlaceholder')}
                    id="link"
                    name="link"
                    type="text"
                    value={bannerForm.values.link}
                    onChange={e => bannerForm.setFieldValue('link', e.target.value)}
                  />
                  {bannerForm.touched.link && bannerForm.errors.link && <span style={{ color: 'red' }}>{bannerForm.errors.link}</span>}
                </div>
                <div
                  className="form__col"
                  style={{ position: 'relative', minWidth: '200px' }}
                >
                  {bannerForm.values.link && (
                    <>
                      <input
                        className="form__control"
                        placeholder={t('bulletinBoards.buttonLinkPlaceholder')}
                        id="textButtonLink"
                        name="textButtonLink"
                        type="text"
                        value={bannerForm.values.textButtonLink}
                        onChange={e => {
                          if (e.target.value.length > 16) return;
                          bannerForm.setFieldValue('textButtonLink', e.target.value);
                        }}
                        maxLength={16}
                        style={{ paddingRight: '40px' }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: '10px',
                          bottom: '10px',
                          fontSize: '12px',
                          color: 'var(--color-neutral)'
                        }}
                      >
                        {bannerForm.values.textButtonLink.length}/16
                      </span>
                    </>
                  )}
                  {bannerForm.touched.textButtonLink && bannerForm.errors.textButtonLink && <span style={{ color: 'red' }}>{bannerForm.errors.textButtonLink}</span>}
                </div>
              </div>
              <span className="form__label">*{t('bulletinBoards.publicationPeriod')}</span>
              <div
                className="form__row form__row--columns"
                style={{ marginTop: '0.5rem' }}
              >
                <div className="form__col">
                  <input
                    className="form__control"
                    id="week"
                    name="week"
                    type="date"
                    value={moment(bannerForm.values.startDate).format('YYYY-MM-DD')}
                    onChange={e => {
                      bannerForm.setFieldValue('startDate', e.target.value);
                    }}
                  />
                  {bannerForm.touched.startDate && bannerForm.errors.startDate && <span style={{ color: 'red' }}>{bannerForm.errors.startDate}</span>}
                </div>
                <div className="form__col">
                  <input
                    className="form__control"
                    id="week"
                    name="week"
                    type="date"
                    value={moment(bannerForm.values.endDate).format('YYYY-MM-DD')}
                    onChange={e => {
                      bannerForm.setFieldValue('endDate', e.target.value);
                    }}
                  />
                  {bannerForm.touched.endDate && bannerForm.errors.endDate && <span style={{ color: 'red' }}>{bannerForm.errors.endDate}</span>}
                </div>
              </div>
              <div className="form__row">
                <div className="form__check form__switch">
                  <input
                    className="form__check-input"
                    id="quiz"
                    name="quiz"
                    type="checkbox"
                    checked={bannerForm.values.publish}
                    onClick={() => {
                      bannerForm.setFieldValue('publish', !bannerForm.values.publish);
                    }}
                  />
                  <label htmlFor="quiz">{t('exams.labelAfterCreate')}</label>
                </div>
              </div>
              <span className="form__text">
                <strong>*Campos Obrigatórios</strong>
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="btn btn--primary"
            style={{ width: 'fit-content', minWidth: '200px' }}
          >
            {t('button.save')}
          </button>
        </form>
      </div>

      {/* <aside className="page__aside" /> */}
    </div>
  );
}
